/**
 * MITRE D3FEND Defensive Techniques
 * Static snapshot for mitigation recommendations
 * Reference: https://d3fend.mitre.org/
 *
 * ISO 27001 Control References: Updated to ISO 27001:2022 Annex A
 * Mapping from 2013 to 2022:
 * - A.9.x.x (Access control) → A.5.x, A.8.x
 * - A.10.x.x (Cryptography) → A.8.x
 * - A.12.x.x (Operations security) → A.8.x
 * - A.13.x.x (Communications security) → A.8.x
 * - A.14.x.x (System acquisition) → A.8.x
 * - A.17.x.x (Business continuity) → A.5.x
 * - A.18.x.x (Compliance) → A.5.x
 */
/**
 * D3FEND Tactics (Defensive categories)
 */
export const D3FEND_TACTICS = [
    {
        id: 'Model',
        name: 'Model',
        description: 'Techniques that create representations of systems to understand their behavior.',
    },
    {
        id: 'Harden',
        name: 'Harden',
        description: 'Techniques that reduce the attack surface by eliminating potential attack vectors.',
    },
    {
        id: 'Detect',
        name: 'Detect',
        description: 'Techniques that identify the presence or occurrence of an attack.',
    },
    {
        id: 'Isolate',
        name: 'Isolate',
        description: 'Techniques that prevent or limit the extent or spread of an attack.',
    },
    {
        id: 'Deceive',
        name: 'Deceive',
        description: 'Techniques that advertise, entice, or allow potential attacks in order to observe them or divert them away from valuable assets.',
    },
    {
        id: 'Evict',
        name: 'Evict',
        description: 'Techniques that remove unauthorized elements from a system or network.',
    },
];
/**
 * D3FEND Techniques - Common defensive techniques for CMDB mitigations
 * OntologyName is extracted from official MITRE D3FEND ontology
 */
export const D3FEND_TECHNIQUES = [
    // ============================================================================
    // Authentication & Access Control (Harden)
    // ============================================================================
    {
        id: 'D3-MFA',
        name: 'Multi-factor Authentication',
        ontologyName: 'Multi-factorAuthentication',
        tacticId: 'Harden',
        description: 'Require multiple forms of authentication before granting access.',
        counters: ['T1078', 'T1110', 'T1133', 'T1021'],
        isoControls: ['A.8.5'], // ISO 27001:2022 - Secure authentication
    },
    {
        id: 'D3-UAP',
        name: 'User Account Permissions',
        ontologyName: 'UserAccountPermissions',
        tacticId: 'Harden',
        description: 'Restrict access to resources based on user authorization level.',
        counters: ['T1078', 'T1098', 'T1136'],
        isoControls: ['A.5.15', 'A.5.16', 'A.5.18', 'A.8.3'], // ISO 27001:2022 - Access control, Identity management, Access rights
    },
    {
        id: 'D3-CH',
        name: 'Credential Hardening',
        ontologyName: 'CredentialHardening',
        tacticId: 'Harden',
        description: 'Strengthen credential storage and handling.',
        counters: ['T1552', 'T1555', 'T1528', 'T1558'],
        isoControls: ['A.5.17', 'A.8.5'], // ISO 27001:2022 - Authentication information, Secure authentication
    },
    {
        id: 'D3-SPP',
        name: 'Strong Password Policy',
        ontologyName: 'StrongPasswordPolicy',
        tacticId: 'Harden',
        description: 'Enforce strong password requirements for user accounts.',
        counters: ['T1110', 'T1078'],
        isoControls: ['A.5.17', 'A.8.5'], // ISO 27001:2022 - Authentication information
    },
    {
        id: 'D3-LFP',
        name: 'Local File Permissions',
        ontologyName: 'LocalFilePermissions',
        tacticId: 'Harden',
        description: 'Configure file system permissions to restrict access.',
        counters: ['T1530', 'T1213', 'T1087', 'T1059'],
        isoControls: ['A.8.3', 'A.8.4'], // ISO 27001:2022 - Information access restriction, Access to source code
    },
    {
        id: 'D3-AL',
        name: 'Account Locking',
        ontologyName: 'AccountLocking',
        tacticId: 'Evict',
        description: 'Lock user accounts after failed authentication attempts or suspicious activity.',
        counters: ['T1110', 'T1078'],
        isoControls: ['A.8.5'], // ISO 27001:2022 - Secure authentication
    },
    {
        id: 'D3-CRO',
        name: 'Credential Rotation',
        ontologyName: 'CredentialRotation',
        tacticId: 'Harden',
        description: 'Regularly rotate credentials to limit exposure window.',
        counters: ['T1552', 'T1555'],
        isoControls: ['A.5.17'], // ISO 27001:2022 - Authentication information
    },
    // ============================================================================
    // Network Security (Isolate/Detect)
    // ============================================================================
    {
        id: 'D3-NTF',
        name: 'Network Traffic Filtering',
        ontologyName: 'NetworkTrafficFiltering',
        tacticId: 'Isolate',
        description: 'Filter network traffic to block malicious or unauthorized communications.',
        counters: ['T1190', 'T1133', 'T1046', 'T1021', 'T1048', 'T1071', 'T1105'],
        isoControls: ['A.8.20', 'A.8.21', 'A.8.22'], // ISO 27001:2022 - Networks security, Security of network services, Segregation of networks
    },
    {
        id: 'D3-ITF',
        name: 'Inbound Traffic Filtering',
        ontologyName: 'InboundTrafficFiltering',
        tacticId: 'Isolate',
        description: 'Filter inbound network traffic at network boundaries.',
        counters: ['T1190', 'T1133', 'T1046'],
        isoControls: ['A.8.20', 'A.8.21'], // ISO 27001:2022 - Networks security
    },
    {
        id: 'D3-OTF',
        name: 'Outbound Traffic Filtering',
        ontologyName: 'OutboundTrafficFiltering',
        tacticId: 'Isolate',
        description: 'Filter outbound network traffic to prevent data exfiltration.',
        counters: ['T1048', 'T1567', 'T1071'],
        isoControls: ['A.8.20', 'A.8.22'], // ISO 27001:2022 - Networks security, Segregation of networks
    },
    {
        id: 'D3-DNSAL',
        name: 'DNS Allowlisting',
        ontologyName: 'DNSAllowlisting',
        tacticId: 'Isolate',
        description: 'Restrict DNS queries to approved domains only.',
        counters: ['T1071', 'T1567'],
        isoControls: ['A.8.20', 'A.8.22'], // ISO 27001:2022 - Networks security
    },
    {
        id: 'D3-DNSDL',
        name: 'DNS Denylisting',
        ontologyName: 'DNSDenylisting',
        tacticId: 'Isolate',
        description: 'Block DNS queries to known malicious domains.',
        counters: ['T1071', 'T1567'],
        isoControls: ['A.8.20'], // ISO 27001:2022 - Networks security
    },
    {
        id: 'D3-DNSTA',
        name: 'DNS Traffic Analysis',
        ontologyName: 'DNSTrafficAnalysis',
        tacticId: 'Detect',
        description: 'Analyze DNS traffic for suspicious patterns.',
        counters: ['T1071', 'T1567'],
        isoControls: ['A.8.15', 'A.8.16'], // ISO 27001:2022 - Logging, Monitoring activities
    },
    {
        id: 'D3-NTSA',
        name: 'Network Traffic Signature Analysis',
        ontologyName: 'NetworkTrafficSignatureAnalysis',
        tacticId: 'Detect',
        description: 'Analyze network traffic for suspicious patterns and signatures.',
        counters: ['T1567', 'T1048', 'T1071', 'T1499'],
        isoControls: ['A.8.15', 'A.8.16', 'A.8.20'], // ISO 27001:2022 - Logging, Monitoring, Networks security
    },
    // ============================================================================
    // Application & Process Security (Harden/Detect)
    // ============================================================================
    {
        id: 'D3-PSEP',
        name: 'Process Segment Execution Prevention',
        ontologyName: 'ProcessSegmentExecutionPrevention',
        tacticId: 'Harden',
        description: 'Prevent execution of code in specific memory segments.',
        counters: ['T1190', 'T1203', 'T1068'],
        isoControls: ['A.8.25', 'A.8.29'], // ISO 27001:2022 - Secure development lifecycle, Security testing
    },
    {
        id: 'D3-EXAL',
        name: 'Executable Allowlisting',
        ontologyName: 'ExecutableAllowlisting',
        tacticId: 'Harden',
        description: 'Only allow approved executables to run.',
        counters: ['T1059', 'T1204', 'T1566'],
        isoControls: ['A.8.19', 'A.8.7'], // ISO 27001:2022 - Installation of software, Protection against malware
    },
    {
        id: 'D3-EDL',
        name: 'Executable Denylisting',
        ontologyName: 'ExecutableDenylisting',
        tacticId: 'Harden',
        description: 'Block known malicious executables from running.',
        counters: ['T1059', 'T1204', 'T1566'],
        isoControls: ['A.8.7'], // ISO 27001:2022 - Protection against malware
    },
    {
        id: 'D3-SU',
        name: 'Software Update',
        ontologyName: 'SoftwareUpdate',
        tacticId: 'Harden',
        description: 'Keep software up to date to patch vulnerabilities.',
        counters: ['T1190', 'T1203', 'T1068', 'T1566.001'],
        isoControls: ['A.8.8', 'A.8.32'], // ISO 27001:2022 - Management of technical vulnerabilities, Change management
    },
    {
        id: 'D3-SBV',
        name: 'Service Binary Verification',
        ontologyName: 'ServiceBinaryVerification',
        tacticId: 'Detect',
        description: 'Verify integrity of service binaries.',
        counters: ['T1543', 'T1574'],
        isoControls: ['A.8.19', 'A.8.9'], // ISO 27001:2022 - Installation of software, Configuration management
    },
    {
        id: 'D3-DA',
        name: 'Dynamic Analysis',
        ontologyName: 'DynamicAnalysis',
        tacticId: 'Detect',
        description: 'Execute and analyze code in a controlled environment.',
        counters: ['T1566.001', 'T1204'],
        isoControls: ['A.8.7', 'A.8.29'], // ISO 27001:2022 - Protection against malware, Security testing
    },
    {
        id: 'D3-FIM',
        name: 'File Integrity Monitoring',
        ontologyName: 'FileIntegrityMonitoring',
        tacticId: 'Detect',
        description: 'Monitor files for unauthorized modifications.',
        counters: ['T1565', 'T1070'],
        isoControls: ['A.8.15', 'A.8.9'], // ISO 27001:2022 - Logging, Configuration management
    },
    // ============================================================================
    // Data Protection (Harden)
    // ============================================================================
    {
        id: 'D3-DENCR',
        name: 'Disk Encryption',
        ontologyName: 'DiskEncryption',
        tacticId: 'Harden',
        description: 'Encrypt data stored on disk.',
        counters: ['T1530', 'T1486', 'T1005'],
        isoControls: ['A.8.24', 'A.8.10'], // ISO 27001:2022 - Use of cryptography, Deletion of information
    },
    {
        id: 'D3-FENCR',
        name: 'File Encryption',
        ontologyName: 'FileEncryption',
        tacticId: 'Harden',
        description: 'Encrypt individual files or file systems.',
        counters: ['T1530', 'T1486'],
        isoControls: ['A.8.24'], // ISO 27001:2022 - Use of cryptography
    },
    {
        id: 'D3-RF',
        name: 'Restore File',
        ontologyName: 'RestoreFile',
        tacticId: 'Evict',
        description: 'Restore files from backup for recovery.',
        counters: ['T1486', 'T1485', 'T1490'],
        isoControls: ['A.8.13', 'A.5.30'], // ISO 27001:2022 - Information backup, ICT readiness for business continuity
    },
    // ============================================================================
    // Monitoring & Logging (Detect)
    // ============================================================================
    {
        id: 'D3-LAM',
        name: 'Local Account Monitoring',
        ontologyName: 'LocalAccountMonitoring',
        tacticId: 'Detect',
        description: 'Monitor local account activity for anomalies.',
        counters: ['T1136', 'T1098', 'T1078'],
        isoControls: ['A.8.15', 'A.8.16'], // ISO 27001:2022 - Logging, Monitoring activities
    },
    {
        id: 'D3-DAM',
        name: 'Domain Account Monitoring',
        ontologyName: 'DomainAccountMonitoring',
        tacticId: 'Detect',
        description: 'Monitor domain account activity for anomalies.',
        counters: ['T1136', 'T1098', 'T1078'],
        isoControls: ['A.8.15', 'A.8.16'], // ISO 27001:2022 - Logging, Monitoring activities
    },
    {
        id: 'D3-PSA',
        name: 'Process Spawn Analysis',
        ontologyName: 'ProcessSpawnAnalysis',
        tacticId: 'Detect',
        description: 'Analyze process creation patterns.',
        counters: ['T1059', 'T1106', 'T1204'],
        isoControls: ['A.8.15', 'A.8.16'], // ISO 27001:2022 - Logging, Monitoring activities
    },
    {
        id: 'D3-SJA',
        name: 'Scheduled Job Analysis',
        ontologyName: 'ScheduledJobAnalysis',
        tacticId: 'Detect',
        description: 'Monitor and analyze scheduled tasks and jobs.',
        counters: ['T1053'],
        isoControls: ['A.8.15', 'A.8.16'], // ISO 27001:2022 - Logging, Monitoring activities
    },
    {
        id: 'D3-SDM',
        name: 'System Daemon Monitoring',
        ontologyName: 'SystemDaemonMonitoring',
        tacticId: 'Detect',
        description: 'Monitor system daemons for unauthorized changes.',
        counters: ['T1543'],
        isoControls: ['A.8.15', 'A.8.16'], // ISO 27001:2022 - Logging, Monitoring activities
    },
    // ============================================================================
    // Inventory & Modeling (Model)
    // ============================================================================
    {
        id: 'D3-SI',
        name: 'Software Inventory',
        ontologyName: 'SoftwareInventory',
        tacticId: 'Model',
        description: 'Maintain an inventory of installed software.',
        counters: ['T1072', 'T1195'],
        isoControls: ['A.5.9', 'A.8.9'], // ISO 27001:2022 - Inventory of information, Configuration management
    },
    {
        id: 'D3-HCI',
        name: 'Hardware Component Inventory',
        ontologyName: 'HardwareComponentInventory',
        tacticId: 'Model',
        description: 'Maintain an inventory of hardware components.',
        counters: ['T1200'],
        isoControls: ['A.5.9', 'A.7.9'], // ISO 27001:2022 - Inventory of information, Security of assets off-premises
    },
    {
        id: 'D3-CI',
        name: 'Configuration Inventory',
        ontologyName: 'ConfigurationInventory',
        tacticId: 'Model',
        description: 'Maintain an inventory of system configurations.',
        counters: ['T1562', 'T1112'],
        isoControls: ['A.8.9'], // ISO 27001:2022 - Configuration management
    },
    {
        id: 'D3-AVE',
        name: 'Asset Vulnerability Enumeration',
        ontologyName: 'AssetVulnerabilityEnumeration',
        tacticId: 'Model',
        description: 'Identify and enumerate vulnerabilities in assets.',
        counters: ['T1190', 'T1203'],
        isoControls: ['A.8.8'], // ISO 27001:2022 - Management of technical vulnerabilities
    },
    {
        id: 'D3-SVA',
        name: 'System Vulnerability Assessment',
        ontologyName: 'SystemVulnerabilityAssessment',
        tacticId: 'Model',
        description: 'Assess systems for security vulnerabilities.',
        counters: ['T1190', 'T1203', 'T1068'],
        isoControls: ['A.8.8', 'A.8.34'], // ISO 27001:2022 - Management of technical vulnerabilities, Protection of information systems during audit testing
    },
    // ============================================================================
    // Email & Content Security (Isolate/Detect)
    // ============================================================================
    {
        id: 'D3-EF',
        name: 'Email Filtering',
        ontologyName: 'EmailFiltering',
        tacticId: 'Isolate',
        description: 'Filter emails to block phishing and malware.',
        counters: ['T1566', 'T1566.001', 'T1566.002'],
        isoControls: ['A.8.7', 'A.8.23'], // ISO 27001:2022 - Protection against malware, Web filtering
    },
    {
        id: 'D3-UA',
        name: 'URL Analysis',
        ontologyName: 'URLAnalysis',
        tacticId: 'Detect',
        description: 'Analyze URLs for malicious content.',
        counters: ['T1566.002', 'T1204.001'],
        isoControls: ['A.8.23'], // ISO 27001:2022 - Web filtering
    },
    {
        id: 'D3-URA',
        name: 'URL Reputation Analysis',
        ontologyName: 'URLReputationAnalysis',
        tacticId: 'Detect',
        description: 'Check URLs against reputation databases.',
        counters: ['T1566.002', 'T1204.001'],
        isoControls: ['A.8.23'], // ISO 27001:2022 - Web filtering
    },
    // ============================================================================
    // Deception (Deceive)
    // ============================================================================
    {
        id: 'D3-DE',
        name: 'Decoy Environment',
        ontologyName: 'DecoyEnvironment',
        tacticId: 'Deceive',
        description: 'Create decoy systems to detect and study attackers.',
        counters: ['T1046', 'T1595'],
        isoControls: ['A.8.16'], // ISO 27001:2022 - Monitoring activities
    },
    {
        id: 'D3-DF',
        name: 'Decoy File',
        ontologyName: 'DecoyFile',
        tacticId: 'Deceive',
        description: 'Create decoy files to detect unauthorized access.',
        counters: ['T1083', 'T1005'],
        isoControls: ['A.8.16'], // ISO 27001:2022 - Monitoring activities
    },
    {
        id: 'D3-DUC',
        name: 'Decoy User Credential',
        ontologyName: 'DecoyUserCredential',
        tacticId: 'Deceive',
        description: 'Create decoy credentials to detect credential theft.',
        counters: ['T1552', 'T1555'],
        isoControls: ['A.8.16'], // ISO 27001:2022 - Monitoring activities
    },
];
/**
 * Helper functions
 */
export function getD3fendTacticById(id) {
    return D3FEND_TACTICS.find(t => t.id === id);
}
export function getD3fendTechniqueById(id) {
    return D3FEND_TECHNIQUES.find(t => t.id === id);
}
export function getD3fendByTactic(tacticId) {
    return D3FEND_TECHNIQUES.filter(t => t.tacticId === tacticId);
}
export function getD3fendCountersForAttack(attackTechniqueId) {
    return D3FEND_TECHNIQUES.filter(t => t.counters.includes(attackTechniqueId));
}
/**
 * Get D3FEND techniques that map to a specific ISO 27001:2022 control
 * @param isoControlId - ISO 27001:2022 control ID (e.g., "A.8.5")
 * @returns Array of D3FEND techniques that implement this control
 */
export function getD3fendByIsoControl(isoControlId) {
    return D3FEND_TECHNIQUES.filter(t => t.isoControls.includes(isoControlId));
}
/**
 * Get the best matching D3FEND technique for an ISO control
 * Returns the first match (most relevant) or undefined if no match
 */
export function getBestD3fendForIsoControl(isoControlId) {
    const matches = getD3fendByIsoControl(isoControlId);
    return matches.length > 0 ? matches[0] : undefined;
}
export function searchD3fend(query) {
    const lowerQuery = query.toLowerCase();
    return D3FEND_TECHNIQUES.filter(t => t.id.toLowerCase().includes(lowerQuery) ||
        t.name.toLowerCase().includes(lowerQuery) ||
        t.description.toLowerCase().includes(lowerQuery));
}
/**
 * Get D3FEND reference URL using official MITRE ontology format
 * @param technique - D3FEND technique object or technique ID
 * @returns URL to the technique on d3fend.mitre.org
 */
export function getD3fendUrl(technique) {
    if (typeof technique === 'string') {
        const found = getD3fendTechniqueById(technique);
        if (found) {
            return `https://d3fend.mitre.org/technique/d3f:${found.ontologyName}`;
        }
        // Fallback - try to use the ID directly
        return `https://d3fend.mitre.org/technique/d3f:${technique}`;
    }
    return `https://d3fend.mitre.org/technique/d3f:${technique.ontologyName}`;
}
